import React, { useState, useEffect } from "react";
import { Row, Col, Card, Alert, Spinner, Form, Button } from "react-bootstrap";
import APIFunction from '../util/api';
import HelperFunction from '../util/helper';
import { SYSTEM_DEFAULTS } from '../util/constants';

export default function Profile() {
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchUserData();
    }, []);

    const fetchUserData = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await APIFunction.getUserSummery();
            setUserData(response);
        } catch (err) {
            setError(err.message || 'An error occurred while loading user data');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="danger" className="m-3">
                <Alert.Heading>Error Loading Profile</Alert.Heading>
                <p>{error}</p>
                <Button variant="outline-danger" size="sm" onClick={fetchUserData}>
                    Retry
                </Button>
            </Alert>
        );
    }

    if (!userData) {
        return (
            <Alert variant="warning" className="m-3">
                No user data available
            </Alert>
        );
    }

    return (
        <>
            {/* Profile Header Card */}
            <Card className="transactions-card mb-4">
                <Card.Body>
                    <div className="card-header-custom">
                        <h5>
                            <i className="bi bi-person-circle me-2"></i>
                            Profile Information
                        </h5>
                    </div>
                    
                    <Row className="mt-4">
                        <Col md={3} className="text-center mb-4">
                            <div className="user-avatar" style={{ 
                                width: '120px', 
                                height: '120px', 
                                margin: '0 auto',
                                fontSize: '3rem',
                                backgroundColor: 'var(--bs-primary-light)',
                                color: 'var(--bs-primary)'
                            }}>
                                <i className="bi bi-person-fill"></i>
                            </div>
                            <h5 className="mt-3 mb-0">
                                {userData.first_name} {userData.last_name}
                            </h5>
                            <p className="text-muted small">{userData.email}</p>
                        </Col>

                        <Col md={9}>
                            <Row>
                                <Col md={6} className="mb-3">
                                    <label className="text-muted small mb-1">First Name</label>
                                    <div className="p-2 bg-light rounded">
                                        <strong>{userData.first_name || 'N/A'}</strong>
                                    </div>
                                </Col>
                                <Col md={6} className="mb-3">
                                    <label className="text-muted small mb-1">Last Name</label>
                                    <div className="p-2 bg-light rounded">
                                        <strong>{userData.last_name || 'N/A'}</strong>
                                    </div>
                                </Col>
                                <Col md={6} className="mb-3">
                                    <label className="text-muted small mb-1">Email Address</label>
                                    <div className="p-2 bg-light rounded">
                                        <strong>{userData.email || 'N/A'}</strong>
                                    </div>
                                </Col>
                                <Col md={6} className="mb-3">
                                    <label className="text-muted small mb-1">Phone Number</label>
                                    <div className="p-2 bg-light rounded">
                                        <strong>{userData.phone || 'N/A'}</strong>
                                    </div>
                                </Col>
                                <Col md={6} className="mb-3">
                                    <label className="text-muted small mb-1">Date of Birth</label>
                                    <div className="p-2 bg-light rounded">
                                        <strong>{userData.date_of_birth ? HelperFunction.formatDate(userData.date_of_birth) : 'N/A'}</strong>
                                    </div>
                                </Col>
                                <Col md={6} className="mb-3">
                                    <label className="text-muted small mb-1">Gender</label>
                                    <div className="p-2 bg-light rounded">
                                        <strong>{userData.gender || 'N/A'}</strong>
                                    </div>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Account Details Card */}
            <Row>
                <Col md={6} className="mb-4">
                    <Card className="stat-card">
                        <Card.Body>
                            <div className="card-header-custom">
                                <h6>
                                    <i className="bi bi-shield-check me-2"></i>
                                    Account Status
                                </h6>
                            </div>
                            <Row className="mt-3">
                                <Col xs={6} className="mb-3">
                                    <label className="text-muted small mb-1">Account Status</label>
                                    <div>
                                        {userData.active ? (
                                            <span className="status-badge status-completed">
                                                <i className="bi bi-check-circle-fill me-1"></i>
                                                Active
                                            </span>
                                        ) : (
                                            <span className="status-badge status-pending">
                                                <i className="bi bi-exclamation-circle-fill me-1"></i>
                                                Inactive
                                            </span>
                                        )}
                                    </div>
                                </Col>
                                <Col xs={6} className="mb-3">
                                    <label className="text-muted small mb-1">Verification</label>
                                    <div>
                                        {userData.verified ? (
                                            <span className="status-badge status-completed">
                                                <i className="bi bi-patch-check-fill me-1"></i>
                                                Verified
                                            </span>
                                        ) : (
                                            <span className="status-badge status-pending">
                                                <i className="bi bi-clock-fill me-1"></i>
                                                Pending
                                            </span>
                                        )}
                                    </div>
                                </Col>
                                <Col xs={12} className="mb-2">
                                    <label className="text-muted small mb-1">Member Since</label>
                                    <div className="p-2 bg-light rounded">
                                        <strong>
                                            <i className="bi bi-calendar-event me-2"></i>
                                            {userData.createdAt ? HelperFunction.formatDate(userData.createdAt) : 'N/A'}
                                        </strong>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={6} className="mb-4">
                    <Card className="stat-card">
                        <Card.Body>
                            <div className="card-header-custom">
                                <h6>
                                    <i className="bi bi-geo-alt me-2"></i>
                                    Address Information
                                </h6>
                            </div>
                            <div className="mt-3">
                                <div className="mb-3">
                                    <label className="text-muted small mb-1">Address</label>
                                    <div className="p-2 bg-light rounded">
                                        <strong>{userData.address || 'Not provided'}</strong>
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="text-muted small mb-1">City</label>
                                    <div className="p-2 bg-light rounded">
                                        <strong>{userData.city || 'Not provided'}</strong>
                                    </div>
                                </div>
                                <div className="mb-2">
                                    <label className="text-muted small mb-1">Country</label>
                                    <div className="p-2 bg-light rounded">
                                        <strong>{userData.country || 'Not provided'}</strong>
                                    </div>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Additional Information Card */}
            <Card className="transactions-card">
                <Card.Body>
                    <div className="card-header-custom">
                        <h6>
                            <i className="bi bi-info-circle me-2"></i>
                            Additional Information
                        </h6>
                    </div>
                    <Row className="mt-3">
                        <Col md={4} className="mb-3">
                            <label className="text-muted small mb-1">User ID</label>
                            <div className="p-2 bg-light rounded">
                                <small className="font-monospace">{userData.id || 'N/A'}</small>
                            </div>
                        </Col>
                        <Col md={4} className="mb-3">
                            <label className="text-muted small mb-1">Last Updated</label>
                            <div className="p-2 bg-light rounded">
                                <small>{userData.updatedAt ? HelperFunction.formatDate(userData.updatedAt) : 'N/A'}</small>
                            </div>
                        </Col>
                        <Col md={4} className="mb-3">
                            <label className="text-muted small mb-1">User Type</label>
                            <div className="p-2 bg-light rounded">
                                <small>{userData.user_type || 'Standard'}</small>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        </>
    );
}
